import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router';
import { Points } from 'components/Points/Points';
import { AvatarWithFlag } from 'components/AvatarWithFlag/AvatarWithFlag';
import EliteBadge from 'components/EliteBadge/EliteBadge';
import {
  HolderRow,
  HolderSortKey,
  HOLDER_SORTS,
  HOLDER_SORT_LABELS,
  activityLabel,
  shortCondition,
  sortHolderRows,
} from './counterpartyPicker';
import './CounterpartyPickerModal.css';

export interface CounterpartyPickerCard {
  name: string;
  setName?: string | null;
  setCode?: string | null;
  collectorNumber?: string | null;
  imageUrl?: string | null;
}

interface CounterpartyPickerModalProps {
  /** Grouped one-row-per-person. The parent fetches and groups; this never refetches. */
  rows: HolderRow[];
  /** Distinct eligible holders — people, not copies. */
  totalHolders: number;
  /** Distinct holders with a priceable copy — the offerable subset. */
  totalAvailableToTrade: number;
  card: CounterpartyPickerCard;
  isMobile: boolean;
  onSelect: (row: HolderRow) => void;
  onClose: () => void;
  /** Element focus returns to when the picker closes on Escape. */
  returnFocusTo?: HTMLElement | null;
}

/**
 * Pick which trader to build an offer with.
 *
 * One component, two presentations: a centered modal on pointer devices and a
 * bottom sheet on touch. The row array, the sort state and the select handler
 * are built once and shared, so the two cannot drift apart — the same discipline
 * CardActionMenu uses for its action array.
 *
 * Rows are PEOPLE. A trader offering this printing in NM and LP is one row
 * showing their best copy, with a quiet "2 copies" indicator; choosing among
 * one person's copies belongs in the builder, not here.
 */
export const CounterpartyPickerModal: React.FC<CounterpartyPickerModalProps> = ({
  rows,
  totalHolders,
  totalAvailableToTrade,
  card,
  isMobile,
  onSelect,
  onClose,
  returnFocusTo,
}) => {
  const location = useLocation();
  const [sortKey, setSortKey] = useState<HolderSortKey>('condition');
  const [activeIndex, setActiveIndex] = useState(0);
  const rowRefs = useRef<Array<HTMLButtonElement | null>>([]);

  // One array, both presentations. Sorting is client-side over the returned
  // page, so switching sorts never refetches and never reorders anything the
  // user cannot already see.
  const sorted = useMemo(() => sortHolderRows(rows, sortKey), [rows, sortKey]);

  // A changed sort leaves activeIndex pointing at a different person; without
  // this the highlight appears to jump to someone the user did not choose.
  useEffect(() => {
    setActiveIndex(0);
  }, [sortKey]);

  // The page behind must not scroll — on desktop as well as touch. (The action
  // menu's own sheet omits this; the picker is a modal dialog and must not.)
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  const closeAndRestore = useCallback(() => {
    onClose();
    returnFocusTo?.focus();
  }, [onClose, returnFocusTo]);

  // Close on route change. Pathname only: /search rewrites its own query string
  // as the user types, and that is not a navigation.
  const pathRef = useRef(location.pathname);
  useEffect(() => {
    if (location.pathname !== pathRef.current) {
      pathRef.current = location.pathname;
      onClose();
    }
  }, [location.pathname, onClose]);

  useEffect(() => {
    const onPopState = () => onClose();
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [onClose]);

  // Same keyboard model as CardActionMenu: arrows wrap, Home/End jump, Enter
  // picks, Escape closes and returns focus to the card that opened the menu.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeAndRestore();
        return;
      }

      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        setActiveIndex((prev) => {
          const step = event.key === 'ArrowDown' ? 1 : -1;
          return (prev + step + sorted.length) % sorted.length;
        });
        return;
      }

      if (event.key === 'Home' || event.key === 'End') {
        event.preventDefault();
        setActiveIndex(event.key === 'Home' ? 0 : sorted.length - 1);
        return;
      }

      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        const row = sorted[activeIndex];
        if (row) onSelect(row);
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [sorted, activeIndex, onSelect, closeAndRestore]);

  useEffect(() => {
    rowRefs.current[activeIndex]?.focus();
  }, [activeIndex, sortKey]);

  // "Most points" sorts on a balance an older backend may not send. Offering a
  // sort that would silently no-op is worse than not offering it, so the option
  // is filtered out rather than disabled — a disabled button still advertises a
  // capability we do not have. `every`, not `some`: a partially populated payload
  // would rank real balances against a 0 fallback and bury those holders for a
  // reason that isn't true.
  const hasBalances =
    sorted.length > 0 && sorted.every((r) => r.holder.pointsBalance !== undefined);
  const availableSorts = HOLDER_SORTS.filter((key) => key !== 'balance' || hasBalances);

  // Every row is the same printing, so language is identical across rows by
  // construction — it describes the list, not any one trader, and belongs in the
  // header rather than repeated on every row. The size === 1 check means a future
  // endpoint widened past one printing silently stops claiming a single language
  // rather than picking one at random.
  const languages = useMemo(
    () => new Set(rows.map((r) => r.holder.lang.toUpperCase())),
    [rows],
  );
  const singleLanguage = languages.size === 1 ? Array.from(languages)[0] : null;

  const sortControl = (
    <div className="counterparty-picker-sorts" role="group" aria-label="Sort traders">
      {availableSorts.map((key) => (
        <button
          key={key}
          type="button"
          className={`counterparty-picker-sort ${key === sortKey ? 'is-active' : ''}`}
          aria-pressed={key === sortKey}
          onClick={() => setSortKey(key)}
        >
          {HOLDER_SORT_LABELS[key]}
        </button>
      ))}
    </div>
  );

  const rowButtons = sorted.map((row, index) => {
    const { holder, copyCount } = row;
    const activity = activityLabel(holder.activity);

    return (
      <button
        key={holder.userId}
        ref={(el) => {
          rowRefs.current[index] = el;
        }}
        type="button"
        className={`counterparty-picker-row ${index === activeIndex ? 'is-active' : ''}`}
        onMouseEnter={() => setActiveIndex(index)}
        onClick={() => onSelect(row)}
      >
        {/* All five elite/avatar fields, deliberately: a partial shape renders an
            avatar that looks right while silently dropping the Elite treatment.
            showAnimatedBorder is the holder's OWN setting and is passed straight
            through — the component ANDs it with isElite, so an Elite trader who
            turned their border off correctly shows none. Never pass `true` here. */}
        <AvatarWithFlag
          avatarUrl={holder.avatarUrl}
          username={holder.username}
          size={38}
          isElite={holder.isElite}
          showAnimatedBorder={holder.showAnimatedBorder}
          borderColor1={holder.borderColor1}
          borderColor2={holder.borderColor2}
          className="counterparty-picker-avatar"
          alt=""
        />

        <span className="counterparty-picker-identity">
          <span className="counterparty-picker-name-line">
            <span className="counterparty-picker-username">{holder.username}</span>
            <EliteBadge isElite={holder.isElite} className="counterparty-picker-elite-badge" />
          </span>
          <span className="counterparty-picker-meta">
            {holder.completedTradeCount} {holder.completedTradeCount === 1 ? 'trade' : 'trades'}
            {activity ? ` · ${activity}` : ''}
          </span>
          {/* The holder's wallet, not this card's price. Labelled because both are
              gold coin pills and mistaking one for the other is expensive. Absent
              (never 0) when the backend has not sent the field. */}
          {holder.pointsBalance !== undefined && (
            <span className="counterparty-picker-balance">
              <span className="counterparty-picker-balance-label">has</span>
              <Points type="small" points={holder.pointsBalance} />
            </span>
          )}
        </span>

        <span className="counterparty-picker-chips">
          <span className="counterparty-picker-chip">{shortCondition(holder.condition)}</span>
          {holder.foil && <span className="counterparty-picker-chip">Foil</span>}
          {copyCount > 1 && (
            <span className="counterparty-picker-copies">{copyCount} copies</span>
          )}
        </span>

        <span className="counterparty-picker-points">
          <Points type="small" points={holder.quotedPricePoints} />
        </span>

        <span className="counterparty-picker-chevron" aria-hidden="true">
          ›
        </span>
      </button>
    );
  });

  // Both numbers are people-counts, so they are directly comparable. The second
  // is shown only when it adds information.
  const countChip = (
    <span className="counterparty-picker-count">
      {totalAvailableToTrade} available to trade
    </span>
  );
  const holdingNote =
    totalHolders > totalAvailableToTrade ? (
      <span className="counterparty-picker-holding">{totalHolders} holding in total</span>
    ) : null;
  const languageNote = singleLanguage ? (
    <span className="counterparty-picker-language">{singleLanguage}</span>
  ) : null;

  const cardLine = [card.setName, card.collectorNumber ? `#${card.collectorNumber}` : null]
    .filter(Boolean)
    .join(' · ');

  // Truncation is effectively unreachable at limit 100, but if it ever happens
  // the count must say so rather than silently presenting a partial list.
  const truncated = sorted.length < totalAvailableToTrade;

  const footer = (
    <div className="counterparty-picker-footer">
      {truncated ? (
        <span className="counterparty-picker-showing">
          Showing {sorted.length} of {totalAvailableToTrade}
        </span>
      ) : (
        <span />
      )}
      <span className="counterparty-picker-reassurance">
        Picking a trader starts an offer, it does not send one
      </span>
    </div>
  );

  if (isMobile) {
    return createPortal(
      <>
        <div className="mobile-sheet-overlay" onClick={onClose} aria-hidden="true" />
        <div
          className="counterparty-picker-sheet"
          role="dialog"
          aria-modal="true"
          aria-label={`Traders with ${card.name}`}
        >
          <button
            type="button"
            className="mobile-sheet-handle"
            onClick={onClose}
            aria-label="Close trader picker"
          >
            <span className="mobile-sheet-handle-bar" />
          </button>

          <div className="counterparty-picker-sheet-header">
            <span className="counterparty-picker-sheet-title">{card.name}</span>
            <span className="counterparty-picker-sheet-sub">
              {totalAvailableToTrade} available
              {singleLanguage ? ` · ${singleLanguage}` : ''}
            </span>
          </div>

          {sortControl}

          <div className="counterparty-picker-sheet-rows">{rowButtons}</div>

          {footer}

          <button type="button" className="counterparty-picker-cancel" onClick={onClose}>
            Cancel
          </button>
        </div>
      </>,
      document.body,
    );
  }

  return createPortal(
    <>
      <div className="counterparty-picker-dimmer" onClick={onClose} aria-hidden="true" />
      <div
        className="counterparty-picker-modal"
        role="dialog"
        aria-modal="true"
        aria-label={`Traders with ${card.name}`}
      >
        <div className="counterparty-picker-header">
          {card.imageUrl && (
            <img
              className="counterparty-picker-thumb"
              src={card.imageUrl}
              alt=""
              aria-hidden="true"
            />
          )}
          <div className="counterparty-picker-header-text">
            <h2 className="counterparty-picker-title">Who do you want to trade with?</h2>
            <div className="counterparty-picker-card-name">{card.name}</div>
            {cardLine && <div className="counterparty-picker-card-line">{cardLine}</div>}
            <div className="counterparty-picker-counts">
              {countChip}
              {holdingNote}
              {languageNote}
            </div>
          </div>
        </div>

        {sortControl}

        <div className="counterparty-picker-rows">{rowButtons}</div>

        {footer}
      </div>
    </>,
    document.body,
  );
};

export default CounterpartyPickerModal;
